import { message } from 'antd';
import { wrapCoordinates } from '../../components/features/Map/MapHelper';
import { IError, IFWEPoint, IResult } from '../../types/api';
import { DeliveryMethodItem } from '../../types/checkout';
import { IDatasetBrief, IGetMetadata } from '../../types/metadata';
import { axiosInstance } from '../Api';

export type CreateReportData = {
  pointName: string;
  datasetIds: string[];
  location: string;
  startTime: string;
  endTime: string;
  timestepInterval: number;
};

export default class Service {
  endPoint = 'Service';

  async retrievePassword(email: string) {
    try {
      const r = await axiosInstance.post<IResult<string>>(
        `/${this.endPoint}/RetrievePassword?email=${email}&domain=MOOD&key=`
      );
      const result = await r.data.Result;
      if (result) {
        return result.startsWith('Invalid')
          ? message.error(result)
          : message.success(result);
      } else {
        message.error('Retreive password failed.');
      }
    } catch (error) {
      const returnedError = error as IError;
      const errorMessage =
        returnedError && returnedError.ErrorMessage
          ? returnedError.ErrorMessage
          : '';
      message.error('Retreive password failed. ' + errorMessage);
    }
  }

  // This API will return immediately with the orderIds and requestIds, with jobs continue running in backend.
  // Monitor progress by calling “CheckFullExtractionStatus” every 2 seconds
  // Meant to be called after the 2 preview APIs. The body is a list of dataset infos,
  // each of them has the same structure of the preview APIs and with the full time range.
  // Please put the 3 objects of the 3 best datasets
  // (time range is returned from previous GetMetadata call).
  async createFullExtractionTasks(
    metadatas: IGetMetadata[],
    coordinates: number[],
    pointName: string,
    gridCellSize: string,
    waterDepth: string,
    briefs: IDatasetBrief[],
    fwePoints: IFWEPoint[],
    timeStepInterval?: number
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    const body = metadatas.map((md: IGetMetadata) => {
      const timeRange = md.TimeDefinition.split(';');
      const brief = briefs.find(
        (brief: IDatasetBrief) => brief.Id === md.DatasetId
      );
      const isFwe = brief?.IsFWE;
      const findFwePoint = isFwe
        ? fwePoints?.find((item) => {
            return (
              coordinates[0] === Number(item.position[0].toFixed(6)) &&
              coordinates[1] === Number(item.position[1].toFixed(6))
            );
          })
        : undefined;
      const name = findFwePoint !== undefined ? findFwePoint.name : pointName;
      return {
        datasetName: md.DatasetId,
        startDate: timeRange[0],
        endDate: timeRange[1],
        location: `${longitude}, ${latitude}`,
        pointName: name,
        gridCellSize: gridCellSize,
        waterDepth: waterDepth,
        isFwe: isFwe,
        TimestepInterval: timeStepInterval ?? 60,
      };
    });
    // always call without userId & key, in order to be consistent when user logs in after this call
    const r = await axiosInstance.post(
      `/ChartNew/SaveCsvExtraction?userId=&key=`,
      body
    );
    const orderAndRequestIds = r.data.Result;
    const enrichedMetadata = metadatas.map(
      (md: IGetMetadata, index: number) => {
        return {
          ...md,
          OrderId: orderAndRequestIds.OrderIdList[index],
          RequestId: orderAndRequestIds.RequestIdList[index],
          CoordinatesString: coordinates.join(),
        };
      }
    );
    return enrichedMetadata;
  }

  // get Delivery Method options
  async GetDeliveryMethod(key: string): Promise<DeliveryMethodItem[]> {
    const r = await axiosInstance.get(`/${this.endPoint}/GetDeliveryMethod`, {
      params: {
        key,
      },
    });
    return r.data.Result;
  }

  async createSubsetExtractionTask(
    datasetName: string,
    startDate: string,
    endDate: string,
    coordinates: number[],
    pointName: string,
    gridCellSize: string,
    waterDepth: string,
    briefs: IDatasetBrief[],
    fwePoints: IFWEPoint[],
    timeStepInterval?: number
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    const brief = briefs.find(
      (brief: IDatasetBrief) => brief.Id === datasetName
    );
    const isFwe = brief?.IsFWE;
    const findFwePoint = isFwe
      ? fwePoints?.find((item) => {
          return (
            coordinates[0] === Number(item.position[0].toFixed(6)) &&
            coordinates[1] === Number(item.position[1].toFixed(6))
          );
        })
      : undefined;
    const name = findFwePoint !== undefined ? findFwePoint.name : pointName;

    const body = [
      {
        datasetName: datasetName,
        startDate: startDate,
        endDate: endDate,
        location: `${longitude}, ${latitude}`,
        pointName: name,
        gridCellSize: gridCellSize,
        waterDepth: waterDepth,
        isFwe: isFwe,
        TimestepInterval: timeStepInterval ?? 60,
      },
    ];
    try {
      const r = await axiosInstance.post(`/ChartNew/SaveCsvExtraction`, body);
      return r.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  async deleteFWEdataset(datasetId: string) {
    const r = await axiosInstance.delete(
      `/${this.endPoint}/DeleteFweData?datasetId=${datasetId}`
    );
    return r.data.Result;
  }

  async canDeleteFWEdata(datasetId: string) {
    const r = await axiosInstance.get(
      `/${this.endPoint}/CanDeleteFweData?datasetId=${datasetId}`
    );
    return r.data.Result;
  }

  async requestDeleteFWEdataset(datasetId: string) {
    const r = await axiosInstance.get(
      `/${this.endPoint}/RequestDeleteFweData?datasetId=${datasetId}`
    );
    return r.data.Result;
  }

  async createReport(body: CreateReportData) {
    try {
      const r = await axiosInstance.post(
        `/${this.endPoint}/CreateReport`,
        body
      );
      return r.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }
}
